import { useEffect, useState } from "react";
import { Info } from "lucide-react";
import { useScoringPreviewMutation } from "@/hooks/useScoringRules";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { QuestionAnswerSimulator } from "./QuestionAnswerSimulator";
import { TopMatchesList } from "./TopMatchesList";
import { ViewportToggle, type Viewport } from "./ViewportToggle";
import type { QuizQuestion } from "@/types/quiz";

interface PreviewPanelProps {
  quizId: string;
  questions: QuizQuestion[];
}

const DEBOUNCE_MS = 500;

export function PreviewPanel({ quizId, questions }: PreviewPanelProps) {
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<string, string>
  >({});
  const [viewport, setViewport] = useState<Viewport>("desktop");

  const debouncedAnswers = useDebouncedValue(selectedAnswers, DEBOUNCE_MS);
  const mutation = useScoringPreviewMutation(quizId);

  useEffect(() => {
    if (Object.keys(debouncedAnswers).length === 0) return;
    mutation.mutate({ selectedAnswers: debouncedAnswers });
    // mutation-Referenz ist instabil pro Render — bewusst nur auf debouncedAnswers triggern
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAnswers]);

  function handleAnswerChange(questionKey: string, answerId: string | null) {
    setSelectedAnswers((prev) => {
      const next = { ...prev };
      if (answerId === null) {
        delete next[questionKey];
      } else {
        next[questionKey] = answerId;
      }
      return next;
    });
  }

  function handleReset() {
    setSelectedAnswers({});
  }

  const hasSelections = Object.keys(selectedAnswers).length > 0;
  const wrapperClass =
    viewport === "mobile" ? "mx-auto max-w-[375px]" : "max-w-full";

  return (
    <section className="space-y-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p>Vorschau der Treffer (nicht Widget-Optik)</p>
        </div>
        <ViewportToggle viewport={viewport} onChange={setViewport} />
      </div>

      <QuestionAnswerSimulator
        questions={questions}
        selectedAnswers={selectedAnswers}
        onAnswerChange={handleAnswerChange}
        onReset={handleReset}
      />

      <div className={wrapperClass} data-testid="preview-viewport">
        <TopMatchesList
          data={mutation.data ?? null}
          isLoading={mutation.isPending}
          hasSelections={hasSelections}
          questions={questions}
        />
        {mutation.isError && (
          <p className="mt-2 text-sm text-red-600">
            Fehler bei der Vorschau:{" "}
            {mutation.error instanceof Error
              ? mutation.error.message
              : "Unbekannter Fehler"}
          </p>
        )}
      </div>
    </section>
  );
}
